import { t } from 'i18next'
import { z } from 'zod'

import { DaysWeekTimesDTO } from '@/modules/companies/domain/models/DaysWeekTimes'
import { HolidayList } from '@/modules/holidays/domain/models/Holiday'
import {
  PersonaInfoSummary,
  PersonaType
} from '@/modules/shared/schemas/PersonaInfoSummary'

import { TicketPriority } from './AreaPriorities'
import { SubArea } from './SubArea'
import { ResponsibleInfo } from './Ticket'

export const Area = z.object({
  id: z.string(),
  name: z.string(),
  areaWeekTimes: z.array(DaysWeekTimesDTO),
  unit: z.object({ id: z.string(), name: z.string() }).nullable(),
  responsible: z.array(
    z.object({
      id: z.string(),
      persona: ResponsibleInfo
    })
  ),
  subArea: z.array(SubArea),
  holidays: HolidayList
})

export const AreaDataTabForm = z.object({
  name: z
    .string()
    .min(1, { message: t('general.form.required') })
    .max(255, { message: t('general.form.maxCaracters', { max: '255' }) }),
  responsible: z
    .array(z.object({ personaId: z.string() }))
    .min(1, { message: t('general.form.atLeastOnePersonRequired') }),
  companyUnitId: z
    .string()
    .min(1, { message: t('general.form.required') })
    .max(255, { message: t('general.form.maxCaracters', { max: '255' }) })
})

export const SubAreaTabForm = z.object({
  subAreaGeneric: z.object({
    id: z.string().nullish(),
    priority: TicketPriority,
    name: z
      .string()
      .min(1, { message: t('general.form.required') })
      .max(255, { message: t('general.form.maxCaracters', { max: '255' }) }),
    personaTypes: z
      .string()
      .array()
      .min(1, { message: t('general.form.atLeastOneRequired') })
  }),
  subArea: z.array(
    z.object({
      priority: TicketPriority,
      name: z
        .string()
        .min(1, { message: t('general.form.required') })
        .max(255, { message: t('general.form.maxCaracters', { max: '255' }) }),
      personaTypes: z
        .string()
        .array()
        .min(1, { message: t('general.form.atLeastOneRequired') }),
      attendants: z.array(z.object({ personaId: z.string() }))
    })
  )
})

export const TabForm = z.union([AreaDataTabForm, SubAreaTabForm])

export const CreateAreaDTO = z.object({
  holidays: HolidayList,
  areaWeekTimes: z.array(DaysWeekTimesDTO),
  ...AreaDataTabForm.shape,
  ...SubAreaTabForm.shape
})

export const AdminUpdateAreaDTO = z.object({
  ...AreaDataTabForm.omit({ companyUnitId: true }).shape,
  subAreaGeneric: z.object({
    id: z.string().nullable(),
    priority: TicketPriority,
    name: z
      .string()
      .min(1, { message: t('general.form.required') })
      .max(255, { message: t('general.form.maxCaracters', { max: '255' }) }),
    personaTypes: z
      .string()
      .array()
      .min(1, { message: t('general.form.atLeastOneRequired') })
  }),
  subArea: z.array(
    z.object({
      id: z.string().nullable(),
      priority: TicketPriority,
      name: z
        .string()
        .min(1, { message: t('general.form.required') })
        .max(255, { message: t('general.form.maxCaracters', { max: '255' }) }),
      personaTypes: z
        .string()
        .array()
        .min(1, { message: t('general.form.atLeastOneRequired') }),
      attendants: z.array(z.object({ personaId: z.string() }))
    })
  ),
  areaWeekTimes: z.array(DaysWeekTimesDTO),
  holidays: HolidayList
})

export const OperationalUpdateAreaDTO = z.object({
  subAreaGeneric: z.object({
    id: z.string().nullable(),
    priority: TicketPriority,
    name: z
      .string()
      .min(1, { message: t('general.form.required') })
      .max(255, { message: t('general.form.maxCaracters', { max: '255' }) }),
    personaTypes: z
      .string()
      .array()
      .min(1, { message: t('general.form.atLeastOneRequired') })
  }),
  subArea: z.array(
    z.object({
      id: z.string().nullable(),
      priority: TicketPriority,
      name: z
        .string()
        .min(1, { message: t('general.form.required') })
        .max(255, { message: t('general.form.maxCaracters', { max: '255' }) }),
      personaTypes: z
        .string()
        .array()
        .min(1, { message: t('general.form.atLeastOneRequired') }),
      attendants: z.array(z.object({ personaId: z.string() }))
    })
  ),
  areaWeekTimes: z.array(DaysWeekTimesDTO),
  holidays: HolidayList
})

export const AreaResponsible = z.object({
  id: z.string(),
  persona: PersonaInfoSummary
})

export const CreateSubAreaForm = z.object({
  id: z.string(),
  name: z.string(),
  priority: TicketPriority,
  personaTypes: z.array(PersonaType),
  attendants: z.array(
    z.object({
      name: z.string(),
      avatarName: z.string(),
      avatar: z.string(),
      personaId: z.string()
    })
  )
})

export const ListOfAreasByName = z.array(
  z.object({
    id: z.string(),
    name: z.string()
  })
)

export type ListOfAreasByName = z.infer<typeof ListOfAreasByName>

export type CreateSubAreaForm = z.infer<typeof CreateSubAreaForm>
export type AreaResponsible = z.infer<typeof AreaResponsible>
export const ListAreas = z.array(Area)
export type CreateAreaDTO = z.infer<typeof CreateAreaDTO>
export type AreaDataTabForm = z.infer<typeof AreaDataTabForm>
export type SubAreaTabForm = z.infer<typeof SubAreaTabForm>
export type TabForm = z.infer<typeof TabForm>
export type ListAreas = z.infer<typeof ListAreas>
export type Area = z.infer<typeof Area>
export type AdminUpdateAreaDTO = z.infer<typeof AdminUpdateAreaDTO>
export type OperationalUpdateAreaDTO = z.infer<typeof OperationalUpdateAreaDTO>
